import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Activity, Smile, BookOpen, Target, Gauge, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';
import { useTheme, useThemeClasses } from "../hooks/useTheme";
import { useUser } from "../context/UserContext";
import { API } from "../utils/constants";
import { RatingSlider } from "../components/RatingSlider";
import { formatRating } from "../utils/ratings";
import RatingMessageField from "../components/RatingMessageField";
import ActivityReminderService from "../utils/ActivityReminderService";
import repairAthleteSessionTimestamps from "../utils/sessionTimestampRepair";

export const RateSessionPage = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { isDark } = useTheme();
  const theme = useThemeClasses(isDark);
  const { user } = useUser();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ratings, setRatings] = useState({ fun: 5, learning: 5, mygoal: 5, intensity: 5 });
  const [saved, setSaved] = useState(false);
  const messageRef = useRef(null);
  const [sentConversation, setSentConversation] = useState('');

  useEffect(() => {
    const fetchSession = async () => {
      try {
        if (user?.id) {
          await repairAthleteSessionTimestamps(user.id);
        }
        const res = await axios.get(`${API}/sessions/${sessionId}`);
        setSession(res.data);
        if (res.data.rating) {
          setRatings({
            fun: res.data.rating.fun || 5,
            learning: res.data.rating.learning || 5,
            mygoal: res.data.rating.mygoal || 5,
            intensity: res.data.rating.intensity || 5
          });
        }
      } catch (e) {
        console.error("Error fetching session:", e);
        toast.error("Session not found");
        navigate("/last-training");
      } finally {
        setLoading(false);
      }
    };
    if (sessionId) fetchSession();
  }, [sessionId, navigate, user?.id]);

  const handleSaveRating = async () => {
    if (!session) return;
    if (messageRef.current && !messageRef.current.validate()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/sessions/${session.id}/rate`, ratings);
      const conversation = await messageRef.current?.send(session.id);
      setSentConversation(conversation || '');
      const reminderActivityId = session.source_activity_id || session.id;
      await ActivityReminderService.cancelActivityRatingReminder(user?.id, reminderActivityId);
      toast.success("Training rated successfully!");
      setSaved(true);
    } catch (e) {
      if (e.message !== "MESSAGE_SEND_FAILED") toast.error("Failed to save rating");
    } finally {
      setSaving(false);
    }
  };

  const ratingTopics = [
    { key: 'fun', label: 'Fun', question: { en: 'Did I enjoy today?', nl: 'Heb ik vandaag genoten?' }, tooltip: { en: 'Did this session feel enjoyable and motivating?', nl: 'Voelde deze sessie leuk en motiverend?' }, icon: Smile, color: 'text-yellow-500' },
    { key: 'learning', label: 'Learning', question: { en: 'Did I grow today?', nl: 'Ben ik vandaag gegroeid?' }, tooltip: { en: 'Did I learn or improve a skill today?', nl: 'Heb ik vandaag een vaardigheid geleerd of verbeterd?' }, icon: BookOpen, color: 'text-blue-500' },
    { key: 'mygoal', label: 'MyGoal', question: { en: 'Did I develop MyGoal?', nl: 'Heb ik mijn MyGoal ontwikkeld?' }, tooltip: { en: 'Did this session move me forward on MyGoal?', nl: 'Heeft deze sessie mij vooruit geholpen op mijn MyGoal?' }, icon: Target, color: 'text-purple-500' },
    { key: 'intensity', label: 'Intensity', question: { en: 'Did I push myself?', nl: 'Heb ik mezelf uitgedaagd?' }, tooltip: { en: 'Did I train with enough effort and focus?', nl: 'Trainde ik met voldoende inzet en focus?' }, icon: Gauge, color: 'text-red-500' }
  ];

  const radarData = [
    { subject: 'Fun', value: ratings.fun, fullMark: 10 },
    { subject: 'Learning', value: ratings.learning, fullMark: 10 },
    { subject: 'MyGoal', value: ratings.mygoal, fullMark: 10 },
    { subject: 'Intensity', value: ratings.intensity, fullMark: 10 }
  ];

  return (
    <div className={`min-h-screen ${theme.bg} flex flex-col transition-colors duration-300`} data-testid="page-rate-session">
      <header className="flex items-center justify-between p-4 pt-6">
        <button data-testid="back-button" onClick={() => navigate("/last-training")} className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${theme.button}`}>
          <ArrowLeft className={`w-5 h-5 ${theme.textPrimary}`} />
        </button>
        <h1 className={`text-xl font-bold ${theme.textPrimary}`}>Rate Training</h1>
        <div className="w-10" />
      </header>

      <div className="flex-1 px-4 pb-8 overflow-y-auto">
        {loading ? (
          <div className={`text-center py-8 ${theme.textMuted}`}>Loading...</div>
        ) : !session ? (
          <div className={`text-center py-12 ${theme.textMuted}`}>Session not found</div>
        ) : saved ? (
          <div className={`text-center py-12`}>
            <div className={`w-20 h-20 mx-auto mb-4 rounded-full ${isDark ? 'bg-green-500/20' : 'bg-green-100'} flex items-center justify-center`}>
              <CheckCircle2 className="w-10 h-10 text-green-500" />
            </div>
            <h3 className={`text-xl font-bold mb-2 ${theme.textPrimary}`}>Rating Saved!</h3>
            <p className={`${theme.textMuted} mb-6`}>{session.name} has been rated</p>
            <div className="flex gap-2 flex-wrap justify-center mb-8">
              <span className={`text-sm px-3 py-1.5 rounded-full ${isDark ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-600'}`}>Fun: {formatRating(ratings.fun)}</span>
              <span className={`text-sm px-3 py-1.5 rounded-full ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>Learn: {formatRating(ratings.learning)}</span>
              <span className={`text-sm px-3 py-1.5 rounded-full ${isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600'}`}>Goal: {formatRating(ratings.mygoal)}</span>
              <span className={`text-sm px-3 py-1.5 rounded-full ${isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600'}`}>Intensity: {formatRating(ratings.intensity)}</span>
            </div>
            <RatingMessageField key={session.id} theme={theme} sessionId={session.id} readOnly />
            {sentConversation && <button onClick={() => navigate(`/chat?conversation=${sentConversation}`)} className={`block mx-auto mb-4 ${theme.textPrimary} underline`}>Open chat with your coach</button>}
            <button onClick={() => navigate("/last-training")} className="px-6 py-3 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-bold rounded-xl">
              Back to Last Training
            </button>
          </div>
        ) : (
          <>
            <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl mb-4`}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#493986]/20 to-[#92a7e8]/20 flex items-center justify-center">
                  <Activity className={`w-6 h-6 ${theme.textPrimary}`} />
                </div>
                <div>
                  <h3 className={`font-semibold ${theme.textPrimary}`}>{session.name}</h3>
                  <p className={`text-sm ${theme.textMuted}`}>{session.type} • {new Date(session.completed_at).toLocaleDateString()} • {session.duration} min</p>
                </div>
              </div>
            </div>

            {/* Rating sliders FIRST */}
            <div className="space-y-3 mb-6">
              {ratingTopics.map(topic => (
                <RatingSlider 
                  key={topic.key} 
                  value={ratings[topic.key]} 
                  onChange={(val) => setRatings({...ratings, [topic.key]: val})} 
                  label={topic.question.en} 
                  infoText={topic.tooltip.en}
                  infoKey={topic.key}
                  icon={topic.icon} 
                  color={topic.color} 
                  isDark={isDark}
                  disabled={saving}
                />
              ))}
            </div>

            <RatingMessageField key={session.id} ref={messageRef} theme={theme} disabled={saving} sessionId={session.id} />

            {/* Radar chart preview BELOW sliders */}
            <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl mb-4`}>
              <h3 className={`font-semibold mb-2 ${theme.textPrimary}`}>Rating Preview</h3>
              <ResponsiveContainer width="100%" height={180}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke={isDark ? 'rgba(255,255,255,0.1)' : 'rgba(73,57,134,0.1)'} />
                  <PolarAngleAxis dataKey="subject" tick={{ fill: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(73,57,134,0.6)', fontSize: 12 }} />
                  <PolarRadiusAxis domain={[0, 10]} tick={{ fill: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(73,57,134,0.4)', fontSize: 10 }} />
                  <Radar name="Rating" dataKey="value" stroke="#493986" fill="#493986" fillOpacity={0.5} />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            <button 
              data-testid="save-rating-button" 
              onClick={handleSaveRating} 
              disabled={saving} 
              className="w-full py-4 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-bold rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all disabled:opacity-50"
            >
              {saving ? "Saving..." : (session.rating ? "Update Rating" : "Save Rating")}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default RateSessionPage;
